import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { GraduationCap, Loader2 } from "lucide-react";
import PortalHeader from "../components/PortalHeader";
import PortalFooter from "../components/PortalFooter";

export default function StudentLogin() {
  const nav = useNavigate();
  const [roll, setRoll] = useState("");
  const [dob, setDob] = useState(""); // <-- Added state for Date of Birth
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!roll.trim()) return toast.error("Enter your roll number");
    if (!dob) return toast.error("Enter your date of birth"); // <-- Added validation for DOB

    setBusy(true);
    try {
      const { data } = await api.post("/student/login", {
        roll_no: roll.trim(),
        dob: dob, // <-- Now sending the actual selected date
      });
      sessionStorage.setItem("student_data", JSON.stringify(data));
      nav("/student/results");
    } catch (e) {
      toast.error(fmtError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-stone-50">
      <PortalHeader />
      <div
        className="flex-1 flex items-center justify-center px-6 py-12 relative"
        style={{
          backgroundImage:
            "linear-gradient(rgba(28, 25, 23, 0.78), rgba(28, 25, 23, 0.78)), url('https://images.pexels.com/photos/19193975/pexels-photo-19193975.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Card className="w-full max-w-md rounded-sm border-stone-200 shadow-xl bg-white p-10 fade-up">
          <img
            src="/gbpiet_logo.png"
            alt="GBPIET"
            className="w-12 h-12 object-contain"
          />
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mt-6 font-semibold">
            GBPIET · Student Portal
          </p>
          <h1 className="font-display text-3xl mt-1">Check your results</h1>
          <p className="text-stone-600 text-sm mt-2">
            Enter your university roll number and date of birth to view all your semester results.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="student-login-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-stone-500">
                University Roll No
              </Label>
              <Input
                value={roll}
                onChange={(e) => setRoll(e.target.value)}
                placeholder="e.g. 230090107001"
                data-testid="student-roll-input"
                className="mt-1 rounded-sm font-mono"
                autoFocus
                required
              />
            </div>
            
            {/* --- NEW DOB INPUT FIELD --- */}
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-stone-500">
                Date of Birth
              </Label>
              <Input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                data-testid="student-dob-input"
                className="mt-1 rounded-sm font-mono text-stone-600 uppercase"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={busy}
              data-testid="student-login-submit"
              className="w-full mt-4 rounded-sm bg-red-900 hover:bg-red-800 h-11"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "View results"
              )}
            </Button>
          </form>
        </Card>
      </div>
      <PortalFooter />
    </div>
  );
}
